import { Badge, BadgeButton } from '@/components/Badge';
import { addRefParam, useReplaceQueryParams } from '@/utils/utils';

import { API_ENDPOINT } from '@/config/endpoint';
import DesktopCard from './DesktopCard';
import { ITechblogDetail } from '@/types/techblog';
import MobileCard from './MobileCard';
import Toast from '@/components/Toast';
import { lastClickedTechblog } from '../techblog.state';
import { messages } from '@/consts/Messages';
import { techblogSearchKeyword } from '../techblog.state';
import themeVariables from '@/styles/themeVariables';
import { useGetFavoriteTechblog } from '@/features/Techblog/hook/api';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';

const TechblogCard = ({
  techblog,
  loading,
}: {
  techblog: ITechblogDetail;
  loading: boolean;
}) => {
  const { id, link } = techblog;

  const queryClient = useQueryClient();

  const setLastClickTechblog = useSetAtom(lastClickedTechblog);

  const { data: favoriteList, refetch } = useGetFavoriteTechblog();

  const setFavoriteList = new Set(favoriteList);
  const bookMarked = setFavoriteList.has(techblog.id);

  const handleCardClick = () => {
    API_ENDPOINT.techblog.viewcount(id);
    API_ENDPOINT.viewcount.increaseTechblog(id);

    setLastClickTechblog({
      id: techblog.id,
      companyId: techblog.company_id,
      title: techblog.title,
      date: techblog.published,
    });

    window.open(addRefParam(link), '_blank');
  };

  const handleBookmarkClicked = async () => {
    try {
      bookMarked
        ? await API_ENDPOINT.techblog
            .deleteFavoriteItem(id)
            .then(() => Toast.success(messages.BOOKMARK_DELETED_SUCCESSFUL))
        : await API_ENDPOINT.techblog
            .addFavoriteItem(id)
            .then(() => Toast.success(messages.BOOKMARK_ADDED_SUCCESSFUL));

      refetch();
      queryClient.invalidateQueries({ queryKey: ['techblog', 'favorite'] });
    } catch (err) {
      Toast.error(messages.UNKNOWN_ERROR);
    }
  };

  return (
    <>
      <MobileCard
        loading={loading}
        handleCardClick={handleCardClick}
        handleBookmarkClicked={handleBookmarkClicked}
        techblog={techblog}
        bookmarkStatus={bookMarked}
      />
      <DesktopCard
        loading={loading}
        handleCardClick={handleCardClick}
        handleBookmarkClicked={handleBookmarkClicked}
        techblog={techblog}
        bookmarkStatus={bookMarked}
      />
    </>
  );
};

export default TechblogCard;

export const PopularTagComponent = ({ isPopular }) => {
  return (
    isPopular && (
      <Badge
        color={themeVariables.colors.blue200}
        backgroundColor={themeVariables.colors.blue100}
        icon={undefined}
        text={'평점 높은 글'}
      />
    )
  );
};

export const TagComponent = ({ tag, ...props }) => {
  const { urlSearchParams, setQueryParams } = useReplaceQueryParams();

  const setKeyword = useSetAtom(techblogSearchKeyword);

  return (
    <>
      {tag &&
        tag.split(',').map((item, index) => (
          <BadgeButton
            key={index}
            text={item}
            onClick={(e) => {
              e.stopPropagation();

              setKeyword({
                keyword: '',
                tag: item,
              });

              urlSearchParams.set('page', '1');
              setQueryParams(urlSearchParams);
            }}
            {...props}
          />
        ))}
    </>
  );
};
